import {
  buildCurrentRange,
  buildFutureRange,
  buildPreviousRange
} from "@/lib/dates.js";

/**
 * The date presets behind the report shortcut rail, and the two operations the
 * rail performs on them: ordering, and resolving one to a date range.
 *
 * Extracted from Reports/Shortcuts.vue so the mobile date sheet offers the same
 * presets with the same meanings rather than a second copy that can drift. The
 * rail reaches roughly 87 `date_range_filter` call sites through
 * components/Datepicker.vue, so a drift here is not local to one screen.
 */

export type DatePeriod = "current" | "previous" | "future";

export type DateRangeUnit =
  "day" | "week" | "month" | "quarter" | "year" | "days";

export interface DatePreset {
  label: string;
  range: DateRangeUnit;
  /** Floats the preset above the multi-day presets. See `orderPresets`. */
  single?: boolean;
  current?: boolean;
  previous?: boolean;
  future?: boolean;
  offset?: [number, number];
}

export const DATE_PRESETS: Record<DatePeriod, DatePreset[]> = {
  current: [
    {
      label: "Today",
      range: "day",
      single: true,
      current: true
    },
    {
      label: "Current Week",
      range: "week"
    },
    {
      label: "Current Month",
      range: "month"
    },
    {
      label: "Current Quarter",
      range: "quarter"
    },
    {
      label: "Current Year",
      range: "year"
    }
  ],
  previous: [
    {
      label: "Yesterday",
      range: "day",
      single: true,
      previous: true
    },
    {
      label: "Previous Week",
      range: "week",
      previous: true
    },
    {
      label: "Previous Month",
      range: "month",
      previous: true
    },
    {
      label: "Previous 30 Days",
      range: "days",
      offset: [0, 30],
      previous: true
    },
    {
      label: "Previous Quarter",
      range: "quarter",
      previous: true
    },
    {
      label: "Previous Year",
      range: "year",
      previous: true
    }
  ],
  future: [
    {
      label: "Tomorrow",
      range: "day",
      single: true,
      future: true
    },
    {
      label: "Next Week",
      range: "week",
      future: true
    },
    {
      label: "Next Month",
      range: "month",
      future: true
    },
    {
      label: "Next 30 Days",
      range: "days",
      offset: [0, 30],
      future: true
    },
    {
      label: "Next Quarter",
      range: "quarter",
      future: true
    },
    {
      label: "Next Year",
      range: "year",
      future: true
    }
  ]
};

// Moved verbatim from Reports/Shortcuts.vue, deliberately. Its return value
// depends only on `a`, so it is not a valid total order and the result it
// produces is a property of the engine's sort rather than of the comparator --
// single-day presets end up above the multi-day ones across all period groups,
// which is not what reading the branches suggests. Rewriting it into something
// that looks equivalent silently reorders every report filter that renders the
// rail, so it is kept as-is and pinned by spec instead.
const orderPresets = (presets: DatePreset[]): DatePreset[] =>
  presets.sort((a, b) => {
    if (a.single) {
      if (a.previous) {
        if (b.current) {
          return 1;
        }
        if (b.future) {
          return -1;
        }
      }
      if (a.future) {
        if (b.current) {
          return 1;
        }
        if (b.previous) {
          return 1;
        }
      }
      return -1;
    }
    return 1;
  });

/** The presets for the requested period groups, in the order the rail shows them. */
export function buildPresetRail(
  periods: DatePeriod[] = ["current", "previous"]
): DatePreset[] {
  const presets = periods.reduce<DatePreset[]>(
    (collected, period) => collected.concat(DATE_PRESETS[period] ?? []),
    []
  );

  return orderPresets(presets);
}

/**
 * The date range a preset stands for.
 *
 * `offset` is passed to every builder, including `buildCurrentRange`, which
 * accepts only a period and therefore discards it. That is the behaviour the
 * rail has today; it is preserved rather than corrected here.
 */
export function resolvePreset(preset: DatePreset): [Date, Date] {
  let rangeFunction = buildCurrentRange;

  if (preset.previous) {
    rangeFunction = buildPreviousRange;
  }
  if (preset.future) {
    rangeFunction = buildFutureRange;
  }

  return rangeFunction(preset.range, preset.offset);
}
